import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { QNA_MAX_IMAGE_LENGTH, QNA_SEND_MESSAGE_MODAL } from '@repo/constants';
import { ICON_CAMERA, ICON_DELETE } from '@repo/assets';
import {
  convertHEICToJPEG,
  getExtensionFromDataURL,
  isImageFile,
  resizeFile,
} from '@repo/utils';
import { Button, Modal } from '@successmode-ktp/kui';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (v: FormData, onSuccess?: () => void) => void;
  passportNumber: string;
  isLoading: boolean;
  lang: 'KOR' | 'ENG';
  isKiosk?: boolean;
  isBill?: boolean;
};

export const SendModal = ({
  isOpen,
  onClose,
  onSubmit,
  passportNumber,
  isLoading,
  lang,
  isKiosk = false,
  isBill = false,
}: Props) => {
  const [content, setContent] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const imagesInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setContent('');
      setImageFiles([]);
      setImages([]);
      setIsSending(false);
    }
  }, [isOpen]);

  const getFormData = useCallback(
    async (payload: { passportNumber: string; content: string }) => {
      const requestBlob = new Blob(
        [
          JSON.stringify({
            ...payload,
            usedAtRealReceipt: isBill,
            fromKioskWeb: isKiosk,
          }),
        ],
        {
          type: 'application/json',
        },
      );
      const formData = new FormData();
      formData.append('request', requestBlob);

      const resizePromise = imageFiles.map(async (file) => {
        return await resizeFile(file);
      });
      const resizedImageFiles = await Promise.all(resizePromise);

      resizedImageFiles.forEach((file: File) => {
        formData.append('files', file, file.name);
      });

      return formData;
    },
    [imageFiles],
  );

  const sendQuestion = async () => {
    if (content.length > 2000) {
      alert('Your inquiry cannot exceed 2000 characters.');
      return;
    }

    if (passportNumber) {
      const formData = await getFormData({ passportNumber, content });
      onSubmit(formData, () => {
        setIsSending(true);
        onClose();
      });
    }
  };

  const parseImages = (files: File[]) => {
    return Promise.all(
      files.map((file) => {
        return new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
      }),
    );
  };

  const onUploadImage = async () => {
    if (imagesInput.current?.files) {
      const files = imagesInput.current.files;
      const remainingSlots = QNA_MAX_IMAGE_LENGTH - imageFiles.length;

      if (files.length > remainingSlots) {
        alert(
          'You have selected the maximum number of photos.\nYou can choose up to 4 photos.',
        );
      } else {
        const convertPromise = [...files].map(async (file) => {
          return file.type === 'image/heic'
            ? await convertHEICToJPEG(file)
            : file;
        });
        const convertedImages = await Promise.all(convertPromise);
        setImageFiles((prev) => [...prev, ...convertedImages]);
        const uploadedImages = await parseImages([...convertedImages]);
        setImages((prev) => [...prev, ...uploadedImages]);
      }
    }
  };

  const handleDeleteImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const handlePopstate = () => {
      setImages([]);
    };
    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  const hasImages = imageFiles.length > 0 && images.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      hasBackdrop
      wrapperStyle={{ minWidth: '340px' }}
      order={10}
    >
      <Modal.Header>{QNA_SEND_MESSAGE_MODAL[lang].title}</Modal.Header>
      {isLoading && (
        <LoadingView>
          <LoadingSpinner />
        </LoadingView>
      )}
      <FaqTextarea
        maxLength={2000}
        placeholder={QNA_SEND_MESSAGE_MODAL[lang].placeholder}
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />
      <div>
        <ImageList>
          {images?.length < QNA_MAX_IMAGE_LENGTH && (
            <ImageUploadButton htmlFor='image-input'>
              <img src={ICON_CAMERA} alt='' width={24} />
              <span>{imageFiles.length}/4</span>
              <ImageInput
                id='image-input'
                type='file'
                onChange={onUploadImage}
                multiple
                ref={imagesInput}
              />
            </ImageUploadButton>
          )}
          {hasImages &&
            images.map((image: any, index: number) => {
              const extension = getExtensionFromDataURL(image);
              return (
                <ImageSection key={index}>
                  {isImageFile(extension) ? (
                    <img src={image} alt='file' />
                  ) : (
                    'FILE'
                  )}
                  <ImageDeleteButton onClick={() => handleDeleteImage(index)}>
                    <img src={ICON_DELETE} alt='Delete image' width={18} />
                  </ImageDeleteButton>
                </ImageSection>
              );
            })}
          {Array.from(
            { length: QNA_MAX_IMAGE_LENGTH - (images?.length || 0) - 1 },
            (_, index) => (
              <ImageSection key={index}></ImageSection>
            ),
          )}
        </ImageList>
        <ImageDescription>
          {QNA_SEND_MESSAGE_MODAL[lang].description}
        </ImageDescription>
      </div>
      <ButtonWrapper>
        <Button type='button' theme='mono' variant='line' onClick={onClose}>
          {QNA_SEND_MESSAGE_MODAL[lang].btnCancel}
        </Button>
        <Button
          type='button'
          onClick={sendQuestion}
          disabled={(content.length === 0 && !hasImages) || isSending}
        >
          {QNA_SEND_MESSAGE_MODAL[lang].btnSend}
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};

export const Spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingView = styled.div`
  position: fixed;
  inset: 0 0 0 0;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 100;
`;

const LoadingSpinner = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ${Spin} 600ms linear infinite;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  border: 5px solid #fff;
  margin: 0 auto;
  border-top: 5px solid #246cf6;
  z-index: 100;
`;

const FaqTextarea = styled.textarea`
  border: none;
  border-radius: 8px;
  width: calc(100% + (100% * 0.1));
  height: 170px;
  padding: calc(16px * 0.9);
  margin-top: 20px;
  font-size: 12px;
  transform: scale(0.9);
  transform-origin: left top;
  font-weight: 400;
  line-height: 150%;
  resize: none;
  outline: none;
  background-color: #f5f6f7;
  ::placeholder {
    color: #5f6165;
  }
`;

const ImageList = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  aspect-ratio: 272/60;
  width: 100%;
  overflow-x: auto;
  margin-bottom: 10px;
`;

const ImageDescription = styled.p`
  font-size: 12px;
  line-height: 18px;
  color: #80848a;
`;

const ImageInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ImageUploadButton = styled.label`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1/1;
  height: 100%;
  background-color: #ffffff;
  border: 1px solid #cbccce;
  border-radius: 12px;
  cursor: pointer;
  & > span {
    font-size: 12px;
    line-height: 18px;
    color: #80848a;
  }
`;

const ImageSection = styled.div`
  position: relative;
  aspect-ratio: 1/1;
  height: 100%;
  background-color: #ffffff;
  border: 1px dashed #cbccce;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImageDeleteButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;
